import { infiniteQueryOptions, keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet, withParams } from './client';
import type { LeaguesScreen, ResultsPage, TournamentScreen, TournamentSquads, TournamentStats } from './leagueTypes';

const tournamentPath = (id: string) => `/api/v5/mobile/screens/cricket/tournament/${id}/`;

export function useLeagues(path: string) {
  return useQuery({
    queryKey: ['leagues', path],
    queryFn: ({ signal }) => apiGet<LeaguesScreen>(path, signal),
    staleTime: 30 * 60_000,
  });
}

/** season — provider_id сезона; не назван → текущий. Чужой сезон — 404 (не подменяем молча). */
export function useTournament(id: string, season: number | undefined) {
  const url = withParams(tournamentPath(id), { season });
  return useQuery({
    queryKey: ['tournament', id, season ?? null],
    queryFn: ({ signal }) => apiGet<TournamentScreen>(url, signal),
    staleTime: 5 * 60_000,
    // смена сезона: шапка прошлого ответа остаётся, пока грузится новый (isPlaceholderData)
    placeholderData: keepPreviousData,
  });
}

/** Продолжение результатов: ?cursor= отдаёт только results + next_cursor. Включается первым «ещё» (enabled). */
/** staleTime 10 мин: сыгранные матчи не меняются, фокус не перезапрашивает все догруженные страницы (HQ-1646). */
export function moreResultsOptions(id: string, season: number | undefined, firstCursor: string | null | undefined, enabled: boolean) {
  return infiniteQueryOptions({
    queryKey: ['tournament-results', id, season ?? null, firstCursor],
    queryFn: ({ pageParam, signal }) =>
      apiGet<ResultsPage>(withParams(tournamentPath(id), { season, cursor: pageParam }), signal),
    initialPageParam: firstCursor ?? '',
    getNextPageParam: (last: ResultsPage) => last.next_cursor ?? undefined,
    enabled: enabled && Boolean(firstCursor),
    staleTime: 10 * 60_000,
  });
}

export const useMoreResults = (id: string, season: number | undefined, firstCursor: string | null | undefined, enabled: boolean) =>
  useInfiniteQuery(moreResultsOptions(id, season, firstCursor, enabled));

export function useTournamentTab<T extends TournamentStats | TournamentSquads>(kind: 'stats' | 'squads', path: string | undefined, season: number | undefined) {
  const url = path ? withParams(path, { season }) : undefined;
  return useQuery({
    queryKey: ['tournament-tab', kind, url],
    queryFn: ({ signal }) => apiGet<T>(url!, signal),
    enabled: Boolean(url),
    staleTime: 5 * 60_000,
  });
}
